/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { IQueueNavigationBinding } from 'app/core/models/essette-interfaces';
import { CaseService } from 'app/cm/services/case-service';
import { apiCall } from 'app/shared/lib/api/api';
import { Toaster } from 'app/shared/lib/toaster';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class RoutingQueueNavigation extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    private caseId!: string;
    private queueTypeCode!: string;
    private queueId!: string;

    private queue: CM.CaseQueue | null = null;
    private allCases: ReadonlyArray<CM.Case> | null = null;

    protected currentCase: CM.Case | null = null;
    protected nextCase: CM.Case | null = null;
    protected previousCase: CM.Case | null = null;


    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async activate({ id, queueTypeCode, queueId }: IQueueNavigationBinding): Promise<void> {
        if(!id) { throw new Error('Missing required property: [id]'); }
        if(!queueId) { throw new Error('Missing required property: [queueId]'); }
        if(id === this.caseId && queueTypeCode === this.queueTypeCode) { return; }

        this.caseId = id;
        this.queueTypeCode = queueTypeCode;
        this.queueId = queueId;

        this.reload();
    }
    protected deactivate(): void { }


    ////****************** Events ***************************************************////



    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(!this.caseId) { throw new Error('Missing required property: [caseId]'); }
        if(!this.queueId) { throw new Error('Missing required property: [queueId]'); }

        if(!this.queue || this.queue.id !== this.queueId) {
            this.loadQueue(this.queueId);
        }

        if(!this.allCases) {
            await this.loadQueueItems(this.queueId);
        }

        this.setQueuePosition(this.caseId);
    }

    /** Finds the current auth in the array and sets the next and previous auth */
    private setQueuePosition(id: string): void {
        if(id && this.allCases) {
            const index = this.allCases.findIndex(a => a.id === id);
            if(index === -1) {
                //this.showNavigation = false;
                return;
            }
            this.currentCase = this.allCases[index];
            const isLastAuth = index === this.allCases.length - 1;
            this.nextCase = isLastAuth ? null : this.allCases[index + 1];
        }
    }

    /** Navigates user to an item id, optionally returning auth from it's queue first */
    protected async getNext(nextCaseId: string | null, returnFromQueue: boolean = false): Promise<void> {
        if(returnFromQueue) {
            const success = await this.returnFromQueue(this.caseId, this.queueId);
            if(!success) { return; }
        }

        if(!!nextCaseId) {
            const { queueTypeCode, queueId } = this;
            this.router.navigateToRoute('cm.case', { caseId: nextCaseId, queueTypeCode, queueId })

            // Removes focus from button (so it stops flashing after navigating)
            const activeElement: HTMLElement | null = <HTMLElement>document.activeElement;
            if(activeElement && activeElement.blur) { activeElement.blur(); }
        }
        else {
            this.returnToQueue();
        }
    }

    private async returnToQueue(): Promise<void> {
        if(this.queueId) {
            this.router.navigate(`#/cm/queues/case/${this.queueId}`);
        }
        else {
            this.toaster.warn().message('Cannot return to queue');
            this.logger.warn('Unable to get queue hyperlink id');
        }
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadQueue(queueId: string): Promise<void> {
        try {
            const response = await this.service.loadQueue(queueId);
            this.queue = response.results[0];
        } catch(error) {
            throw error;
        }
    }

    @apiCall
    private async loadQueueItems(queueId: string): Promise<void> {
        try {
            this.state.beginLoad();
            const query = this.service.createLoadCasesByQueueQuery(queueId, { all: true });
            const response = await this.service.get<CM.Case>(query);
            this.allCases = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async returnFromQueue(caseId: string, queueId: string): Promise<boolean> {
        try {
            this.state.beginSave();
            const response = await this.service.returnFromQueue(caseId, queueId);
            this.toaster.standard.returned('cm:case.$', response.isSuccessful);
            return response.isSuccessful;
        } catch(error) {
            this.toaster.standard.returned('cm:case.$', false);
            this.logger.error(`Unable to return case from queue`, error);
            return false;
        } finally {
            this.state.endSave();
        }
    }

}
